import { useCallback, useEffect, useRef, useState } from 'react';
import { storage } from '../../utils/platform';
import { openExternalAuthFlow, openExternalUrl, urlToWeb } from '../utils/external';
import { API_BASE } from '../../config/api';
import { canShowPurchaseCta } from '../utils/storeCompliance';

async function fetchJson(url, options = {}) {
  const res = await fetch(url, options);
  let payload = null;
  try { payload = await res.json(); } catch (_) {}
  if (!res.ok) {
    const err = new Error(payload?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

export function useSubscription() {
  const [plan, setPlan] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  const [trialEndDate, setTrialEndDate] = useState(null);
  // Résiliation programmée : l'abonnement reste actif jusqu'à currentPeriodEnd.
  // Affiché sur mobile comme une INFORMATION d'état de compte, jamais comme un
  // appel à l'action — cf. App Store 3.1.3(f) et utils/storeCompliance.
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cancelRef = useRef(false);

  const getHeaders = useCallback(async () => {
    const token = await storage.get('token');
    return token
      ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { 'Content-Type': 'application/json' };
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getHeaders();
      const [stripePlan, account] = await Promise.all([
        fetchJson(`${API_BASE}/api/stripe/plan`, { headers }).catch(() => null),
        fetchJson(`${API_BASE}/api/account/status`, { headers }).catch(() => null),
      ]);
      if (cancelRef.current) return;
      setPlan(stripePlan?.plan || null);
      setPlanStatus(stripePlan?.plan_status || null);
      setCancelAtPeriodEnd(stripePlan?.cancel_at_period_end === true);
      setCurrentPeriodEnd(stripePlan?.current_period_end || null);
      setAccountStatus(account?.status || null);
      setTrialEndDate(account?.trial_end_date || null);
    } catch (e) {
      console.error('[useSubscription] fetchAll failed:', e);
      if (!cancelRef.current) setError(e.message || 'Erreur de chargement');
    } finally {
      if (!cancelRef.current) setLoading(false);
    }
  }, [getHeaders]);

  // Pas de Stripe Customer Portal côté backend : on redirige vers la web app.
  // Défense en profondeur : même si un composant oubliait le garde d'affichage,
  // aucune ouverture de parcours de paiement ne part depuis l'app (3.1.3(f)).
  const openPortal = useCallback(() => {
    if (!canShowPurchaseCta()) return;
    openExternalUrl(urlToWeb('/dashboard'));
  }, []);

  const openPricing = useCallback(() => {
    if (!canShowPurchaseCta()) return;
    openExternalUrl(urlToWeb('/tarifs'));
  }, []);

  const openCheckout = useCallback(async (planId) => {
    const headers = await getHeaders();
    const data = await fetchJson(`${API_BASE}/api/stripe/create-checkout-session`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ plan: planId }),
    });
    // Native : Safari View Controller in-app (préserve l'état React pendant le
    // checkout). Web : top-level redirect.
    if (data?.url) await openExternalAuthFlow(data.url);
    return data?.url;
  }, [getHeaders]);

  useEffect(() => {
    cancelRef.current = false;
    fetchAll();
    return () => { cancelRef.current = true; };
  }, [fetchAll]);

  return {
    plan, planStatus, accountStatus, trialEndDate,
    cancelAtPeriodEnd, currentPeriodEnd,
    loading, error,
    refresh: fetchAll,
    openPortal, openPricing, openCheckout,
  };
}
